import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSupabase } from "./client.js";
import { createRefdata } from "./refdata.js";
import { createResync } from "./resync.js";
import { createRealtime } from "./realtime.js";
import { subscribeToIdToken } from "./client.js";
import { buildHierarchies, buildLabelsHierarchy } from "./hierarchy.js";
import { shapeChart, toOrg } from "./rowShapes.js";
import { classifyRpcError, reportIfSystemFault } from "./reportErrors.js";
import { captureDataFault } from "./telemetry.js";
import { publishEvents, publishFullInvalidate } from "./orgEvents.js";

/**
 * Re-raise a PostgREST failure WITHOUT losing its code.
 *
 * Building a plain Error from the interpolated "code: message" string throws
 * the code away as a property and leaves it only as text — which is fatal for
 * src/data/accessGuard.js, whose entire rule is "SQLSTATE 42501 AND the
 * not_a_member sentinel". A guard reading a flattened message cannot tell a
 * revoked member from an anonymous caller, and that misreading fires teardown
 * on healthy sessions.
 *
 * IT IS ALSO WHERE THIS HOOK'S FAILURES BECOME VISIBLE. A failed `org_bootstrap`
 * leaves `loaded` false, the provider's `refSource` falls back to the Firestore
 * listeners on that one test, and the screen looks perfectly healthy — so
 * "is the Postgres path actually working for anyone, or has it been failing all
 * week?" was unanswerable from production, which is precisely the comparison the
 * dual-run wave exists to make.
 *
 * Reported through the SAME `reportIfSystemFault` the RPC seam uses, so there is
 * one convention and no double-reporting: a revoked member is FAULT_USER here
 * and the access guard raises its own event for that teardown, while deploy skew
 * and malformed requests are FAULT_SYSTEM and land as one event with the org and
 * the function name on them.
 */
function rpcFailure(error, { orgId = null, fn = null } = {}) {
  const err = new Error(`${error?.code ?? "error"}: ${error?.message ?? "unknown"}`);
  err.code = error?.code ?? null;
  err.cause = error;
  // Kept on the error so nothing downstream has to re-classify — the same
  // contract src/data/rpc.js's `rpcError` offers.
  err.verdict = classifyRpcError(error);
  reportIfSystemFault(err.verdict, { orgId, fn });
  return err;
}

/**
 * Call `org_bootstrap` and either return its payload or throw with the code
 * intact.
 *
 * Two callers, one contract: the hook's own bootstrap below, and the access
 * guard's `probeAccess` (src/data/accessGuard.js `confirm()`), which needs the
 * authoritative membership answer and nothing else. They must not drift —
 * the guard's whole rule is "SQLSTATE 42501 AND the not_a_member sentinel", so
 * a second call site that flattened the error would silently stop classifying.
 */
export async function probeOrgAccess(orgId) {
  const { data, error } = await getSupabase().rpc("org_bootstrap", { p_org_id: orgId });
  if (error) throw rpcFailure(error, { orgId, fn: "org_bootstrap" });
  return data;
}

/**
 * Compose the N2 data layer into the shape the app shell already consumes.
 *
 * This is the seam between "the layer works" (proven by the module tests and the
 * live harnesses) and "the app uses it". It exists as a hook rather than being
 * inlined into the provider so the two data paths can run SIDE BY SIDE while the
 * Firestore listeners are still in place — the provider picks which one feeds the
 * context, and the other can be compared against it on real data before anything
 * is deleted.
 *
 * DISABLED BY DEFAULT. `enabled` is off unless VITE_USE_POSTGRES_REFDATA is set,
 * so merging this changes nothing until someone opts in. The eight listeners come
 * out only once the two paths have been observed agreeing.
 */
export function useOrgRefdata(orgId, { enabled = false, uid = null, onAccessError, onTransportDegraded } = {}) {
  const [snapshot, setSnapshot] = useState(null);
  const stack = useRef(null);

  // Held in a ref so a provider that re-creates the callback each render does
  // not tear down and re-open the whole stack.
  const onAccessErrorRef = useRef(onAccessError);
  onAccessErrorRef.current = onAccessError;
  // Same reason, for the C17 seam below.
  const onTransportDegradedRef = useRef(onTransportDegraded);
  onTransportDegradedRef.current = onTransportDegraded;

  /**
   * Drop the snapshot and the stack that produced it.
   *
   * The provider's revoked-access teardown (`resetOrgScopedState` and friends)
   * clears the FIRESTORE slices only. Without this, a user removed from an org
   * keeps being served that org's accounts, funds, labels and all three
   * hierarchies out of this hook — the teardown would leave the data standing on
   * exactly the path the guard exists for. Stops the transport too: a revoked
   * member's channel cannot authorize, so leaving it up is a rejoin loop against
   * a door that is now closed.
   *
   * Stable identity: the provider holds it across renders and the guard's
   * once-created callbacks reach it through a ref.
   */
  const reset = useCallback(() => {
    stack.current?.realtime.stop();
    stack.current?.refdata.close();
    stack.current = null;
    setSnapshot(null);
  }, []);

  useEffect(() => {
    // BEFORE the early return, not after it: on an org switch (and on sign-out,
    // where orgId goes null) the previous org's snapshot is still `loaded:true`
    // for the whole bootstrap round trip, and the provider serves whichever
    // source says loaded. One org's chart of accounts rendered under another
    // org's name is the worst thing this layer can do.
    setSnapshot(null);
    if (!enabled || !orgId) return undefined;

    const supabase = getSupabase();

    // refdata owns the slices; resync decides WHEN they are stale; realtime is
    // only the transport. Wiring them here keeps each ignorant of the others.
    const refdata = createRefdata({
      fetchBootstrap: async (id) => {
        try {
          return await probeOrgAccess(id);
        } catch (err) {
          // Raw error first: only the caller's guard can tell revocation from
          // an expired token, and it needs the untouched code.
          onAccessErrorRef.current?.(err.cause ?? err, id);
          throw err;
        }
      },
      onChange: () => {
        const state = refdata.state;
        // Every successful bootstrap re-seeds the cursor if — and only if — the
        // server turns out to be BEHIND it. The rule and the reason live in
        // resync.reconcileBaseline; what matters here is that it happens on
        // every reload rather than only at mount.
        if (state.loaded) resync.reconcileBaseline(state.seq ?? 0);
        setSnapshot(state);
      },
    });

    const resync = createResync({
      fetchEventsSince: async (afterSeq, limit) => {
        const { data, error } = await supabase.rpc("events_since", {
          p_org_id: orgId, p_after_seq: afterSeq, p_limit: limit,
        });
        if (error) {
          onAccessErrorRef.current?.(error, orgId);
          throw rpcFailure(error, { orgId, fn: "events_since" });
        }
        return data ?? [];
      },
      // Reference data first, then the fan-out (src/data/orgEvents.js): the
      // ledger list and any later screen store subscribe there by org id, so
      // this remains the ONE events_since stream and the ONE cursor. Both are
      // fire-and-forget from resync's point of view (resync.js:114 does not
      // await), and publishEvents never throws.
      onInvalidate: (events) => {
        refdata.invalidate(events);
        publishEvents(orgId, events);
      },
      onFullInvalidate: () => {
        refdata.invalidateAll();
        publishFullInvalidate(orgId);
      },

      // The unrecoverable-gap branch, made observable. This is a measurement,
      // not an alarm: the client recovers on its own with one extra bootstrap,
      // and the number that matters is how OFTEN and how BIG, per org — that is
      // what decides whether the org_events retention window is long enough.
      onGap: ({ fromSeq, oldestSeq, missing }) =>
        captureDataFault("resync fell off the event sequence", {
          component: "resync",
          action: "full_invalidate",
          orgId,
          level: "warning",
          // The counts stay OUT of tags on purpose: a gap size is unbounded
          // cardinality, and tagging it would shatter one issue into hundreds.
          context: { from_seq: fromSeq, oldest_seq: oldestSeq, missing_events: missing },
        }),
    });

    // No uid is passed: the `user:{uid}` channel has no producer anywhere in the
    // migration tree, so realtime.js no longer opens it (see its header). `uid`
    // stays in this effect's deps because the WHOLE stack must be rebuilt when
    // the signed-in user changes — a different person is a different bootstrap.
    const realtime = createRealtime({
      supabase,
      resync,
      subscribeToIdToken,
      // C17: a channel stuck at the backoff ceiling and a membership that was
      // revoked are the same observation from here — the join is refused either
      // way and nothing issues an RPC to classify. The provider's guard owns the
      // question; this hook only carries it across the seam.
      onDegraded: () => onTransportDegradedRef.current?.(),
    });

    stack.current = { refdata, resync, realtime };

    let cancelled = false;
    (async () => {
      const state = await refdata.open(orgId);
      if (cancelled) return;
      setSnapshot(state);
      // A FAILED bootstrap must not start the transport. refdata.open swallows
      // its own error and resolves `loaded:false` with no seq, so the two lines
      // below would baseline resync at 0 and join the channel anyway — and the
      // first SUBSCRIBED then calls events_since(after_seq = 0), replaying the
      // org's whole event log in 500-row batches against a store holding no
      // data, on every reconnect. Staying dark is the correct answer: mapSnapshot
      // reports not-loaded, and the provider keeps serving Firestore.
      if (!state.loaded) return;
      // Seed resync from the bootstrap's own sequence — captured server-side
      // BEFORE the slices were read, so nothing that happened during assembly
      // can be missed.
      resync.setBaseline(state.seq ?? 0);
      realtime.start(orgId);
    })();

    return () => {
      cancelled = true;
      realtime.stop();
      refdata.close();
      stack.current = null;
    };
  }, [orgId, enabled, uid]);

  return useMemo(
    () => ({ ...mapSnapshot(snapshot, enabled, orgId), reset }),
    [snapshot, enabled, orgId, reset],
  );
}

/**
 * Wrap a Postgres timestamp in the one Firestore Timestamp method its consumers
 * actually call.
 *
 * subscribe_warning.jsx:119 does `orgData.creationDate?.toDate()` with NO
 * fallback: hand it a Date or an ISO string and it throws "toDate is not a
 * function" inside the free-trial day counter — the one component a trial
 * customer is guaranteed to render. SubscriptionBanner.jsx:202 makes the same
 * call behind a feature test. Nothing calls toMillis, seconds or nanoseconds, so
 * nothing else is faked: a shim that pretends to be more than it is invites the
 * next caller to trust the parts that are missing.
 */
function timestampShim(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : { toDate: () => date };
}

/**
 * The parent/child map org_info.js used to write onto the org document, rebuilt
 * from public.org_hierarchy.
 *
 * Always an object, never null. Its 23 consumers read it as
 * `orgData?.orgHierarchy?.isParent` and `?.childOrgs?.length || 0`, so an absent
 * map and "this org is not a parent" are the same value to them — meaning a
 * missing slice would switch consolidated reporting off for every parent org
 * without a single error. Returning explicit falses says "asked and answered".
 */
function mapHierarchy(hierarchy) {
  return {
    isParent: Boolean(hierarchy?.is_parent),
    isChild: Boolean(hierarchy?.is_child),
    parentOrg: hierarchy?.parent_org ?? null,
    childOrgs: hierarchy?.child_orgs ?? [],
  };
}

/**
 * Explore-trial fields (sample/blank throwaway books that freeze at day 14 and
 * purge at day 44) — NOT the billing free trial, which is org_billing.
 *
 * No row means `trial: false`, stated rather than omitted. TrialBanner.jsx:56,
 * select_org.jsx:49 and SubscriptionBanner.jsx:318 all branch on
 * `orgData?.trial === true`, so false is the correct, complete answer for an
 * ordinary org: no trial banner, no "Trial" chip, and the ordinary subscription
 * banner is NOT suppressed.
 *
 * `trial` and `trialFrozen` are derived, not stored — the row's existence is the
 * first and `frozen_at` is the second (see migration 0008). A boolean stored
 * beside its own timestamp is a pair that can disagree, and this one gates a
 * read-only banner.
 */
function mapTrial(trial) {
  if (!trial) return { trial: false };
  return {
    trial: true,
    trialFlavor: trial.flavor,
    trialSize: trial.size ?? null,
    trialSeedStatus: trial.seed_status,
    trialStartedAt: timestampShim(trial.started_at),
    trialExtendedDays: trial.extended_days ?? 0,
    trialFrozen: trial.frozen_at != null,
    trialFrozenAt: timestampShim(trial.frozen_at),
    trialPurgeAt: timestampShim(trial.purge_at),
  };
}

/**
 * Rebuild the `orgs/{orgId}` DOCUMENT from the relational pieces it was split
 * into: the orgs row, the members map (0007), the hierarchy relation and the
 * trial row (0008).
 *
 * The raw row is spread underneath so a reader of a column name still finds it
 * and the mapping is additive; the camelCase keys layered on top are the actual
 * contract and win every collision. Only the crossings that are NOT a plain
 * rename carry a comment — the rest are mechanical.
 */
function mapOrgDocument(row, snapshot) {
  if (!row) return null;
  return {
    // `financialYearStart` and `featureOverrides` come from here, shared with
    // the report Function's own org read (`src/reports/server/refdata.js`).
    ...toOrg(row),

    orgId: row.id,          // 12 call sites read orgData.orgId, not .id
    orgName: row.name,
    orgType: row.org_type,
    homeCurrency: row.home_currency,
    taxId: row.tax_id,
    streetAddress: row.street_address,
    donationStatementCoverNote: row.donation_statement_cover_note,
    teardownInProgress: row.teardown_in_progress,

    creationDate: timestampShim(row.created_at),

    // orgs.features is the OVERRIDE namespace (schema §S8); the entitlements the
    // UI gates on are v_org_effective_features, which arrives as its own slice.
    // Getting these two the wrong way round is the most expensive mistake in
    // this file: overrides are `{}` for almost every org, so serving them as
    // `features` would read as bankIntegration:false, linkedAccounts:false,
    // consolidatedReporting:false — switching paid features off across the
    // whole estate while every screen still rendered normally.
    features: snapshot.features ?? {},

    // Firestore kept membership INSIDE the org document as a `users` map, and
    // five call sites still index it that way — role gating in org_info.js and
    // FirmManagementDashboard.jsx, the member list, the seat count, the
    // ownership-transfer copy, and computeEffectiveOrgRoles. This schema
    // splits roles into org_members and display fields into users; putting
    // them back under the key the shell expects is cheaper and far safer than
    // rewriting those call sites to a second vocabulary.
    //
    // Without this every signed-in user resolves to zero org roles and
    // canManageOrg() refuses everything — an app that silently goes read-only
    // rather than erroring, which is the worst way for this to fail.
    users: snapshot.members ?? {},

    orgHierarchy: mapHierarchy(snapshot.hierarchy),
    ...mapTrial(snapshot.trial),
  };
}

/**
 * Map Postgres rows into the shapes the context already exposes. Components are
 * not asked to learn a second vocabulary — that is the whole point of doing it
 * here rather than in 40 call sites.
 *
 * Exported and pure so it can be tested against a snapshot literal, without a
 * server, a client, or a rendered hook. Every field below is a contract with an
 * existing consumer, and a test that only proves the hook renders proves nothing
 * about them.
 *
 * WHAT THE ORG DOCUMENT CARRIED THAT NOTHING HERE CAN SERVE. These have no
 * column, view or relation anywhere in the schema, so they come out undefined
 * and every consumer takes its own already-written fallback branch:
 *
 *   debugMode      → IncomeStatement.jsx passes `orgData?.debugMode || false`
 *                    to the report backend. Absent = debug logging off, which is
 *                    the value it has for every org that never turned it on.
 *   logoUrl        → OrgLogoSection.jsx renders the upload placeholder instead
 *                    of the org's logo. Cosmetic, but visible on statements.
 *   mlOverride     → isMlEnabled() (prediction_utils.js:13) loses the admin
 *                    on/off escape hatch and falls through to
 *                    features.mlAutoCategorization, which IS served. Safe
 *                    default; an admin's manual override is simply not honoured.
 *   ttcEnabled /   → the TTC bookkeeper button and TTCDashboard treat the org as
 *   ttcConfig /      TTC-disabled. public.ttc_org_settings holds these facts but
 *   isBookkeepingClient  is not in the bootstrap; adding it belongs with the TTC
 *                    slice, not here.
 *   chosenPath /   → onboarding path memory. Only read while onboarded === false,
 *   onboardingPath   and an org on the Postgres path has already been committed.
 *   subscriptionId / subscriptionCurrent / subscriptionData
 *                  → these were never on the org document at all: the SUBSCRIPTION
 *                    listener merges them into orgData (context_and_index.jsx:732).
 *                    Deleting the org listener does not remove them — switching
 *                    `orgData` to this mapper does, because the merge writes into
 *                    the Firestore state object. They belong to whoever retires
 *                    the subscription listener; see the report.
 */
export function mapSnapshot(snapshot, enabled = true, orgId = null) {
  if (!enabled) return { enabled: false, loaded: false };
  if (!snapshot?.loaded) {
    return { enabled: true, loaded: false, error: snapshot?.error ?? null };
  }
  // A snapshot that is not demonstrably THIS org's is not loaded. `org_id` is
  // the first key org_bootstrap builds (migration 0003), so a payload without
  // one is not a payload this code understands — and the safe reading of "I
  // cannot prove whose data this is" is to serve none of it. Belt to the hook's
  // braces: it clears the snapshot on every org change, and this makes an
  // in-flight response for the previous org unusable even if one lands after.
  if (orgId != null && snapshot.org_id !== orgId) {
    return { enabled: true, loaded: false, error: null };
  }

  return {
    enabled: true,
    loaded: true,
    error: null,
    seq: snapshot.seq ?? 0,
    orgData: mapOrgDocument(snapshot.org, snapshot),
    // Renamed to the vocabulary the app reads, and the hierarchies built from the
    // RAW rows — see `shapeChart` in src/data/rowShapes.js, which the reports
    // Function's five-table read shares so the two cannot drift. `?? []` on every
    // slice keeps all six keys present on a partial payload: this is the app
    // shell's contract and a missing key here is a crash, not a smaller chart.
    ...shapeChart(
      {
        accounts: snapshot.accounts ?? [],
        funds: snapshot.funds ?? [],
        labels: snapshot.labels ?? [],
        accountGroups: snapshot.account_groups ?? [],
        labelGroups: snapshot.label_groups ?? [],
      },
      { buildHierarchies, buildLabelsHierarchy },
    ),
    features: snapshot.features ?? null,
    subscriptionData: snapshot.billing ?? null,
    predictions: snapshot.predictions ?? null,
  };
}


/** Read once, so a flag flip needs a reload rather than leaving two live paths. */
export const POSTGRES_REFDATA_ENABLED =
  import.meta.env?.VITE_USE_POSTGRES_REFDATA === "true";
